/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { styleMap } from 'lit-html/directives/style-map.js';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { baseFontSize } from '../../utils/base-font-size.js';
/**
 * @prop {String} label - Defines the label shown at the top of the pane (won't be shown if the header slot is occupied).
 * @prop {String} behavior - Defines the behavior of the pane. Possible values are `collapse` and `condense`.
 * @prop {String} size - Default `expanded`. Defines the size of the pane. Possible values are `expanded`, `collapsed`, `condensed`, `resizable` or a fixed value (in XXpx, XXrem, XXvw and XX% - percentile values will be converted into XXvw). It is updated upon clicking on the arrow in case any behavior is set. If set to `resizable` the width can be resized in the horizontal direction with a minimum value (352px).
 * @prop {String} arrowPosition - Default center. Defines the position of the arrow in case behavior is set.
 * @slot unnamed slot where the main content is rendered. Takes any component as child.
 * @slot header - Takes children such as Tabs.
 * @slot functions - Takes Icons as children.
 * @slot footer - Takes Buttons as children.
 * @slot condensed - Slot where the condensed content goes. Takes Icons and Tools as children.
 */
export class SHPane extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      behavior: { type: String, reflect: true },
      size: { type: String, reflect: true, value: 'expanded' },
      _fixSize: { type: String },
      arrowPosition: {
        type: String,
        reflect: true,
        value: 'center',
        attribute: 'arrow-position',
      },
      /** @ignore */
      _emptyFooter: { type: Boolean },
      /** @ignore */
      _emptyHeader: { type: Boolean },
      /** @ignore */
      _isResize: { type: Boolean },
    };
  }
  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          z-index: 2; /**
          Setting z-index is necessary here inorder to make the pane appear.
          
          Why the value is set to 2?

          Since
            Nav-bar has z-index=3,
            access-bar has z-index=4,
            a reasonable value of 2 is chosen. 
          
        */
          display: block;
          background-color: var(--base-2);
          transition: var(--transition-time) all ease-in-out;
          box-shadow: var(--shadow-navigation);
          width: unset;
          min-width: unset;
        }
        .pane-wrapper {
          width: 22rem;
          height: 100%;
          display: flex;
          flex-direction: column;
          position: relative;
          transition: var(--transition-time) all ease-in-out;
        }
        .label-wrapper {
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          padding: 1rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
        }
        .header-wrapper {
          padding: 1rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
        }
        .body-wrapper {
          flex: 1;
          padding: 1rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          -ms-overflow-style: -ms-autohiding-scrollbar;
        }
        .body-wrapper > ::slotted(*:not(:first-child):not(sh-menu-item)) {
          margin-top: 0.5rem;
        }
        .condensed-wrapper > ::slotted(*:not(sh-menu-item)) {
          margin-bottom: 0.5rem;
        }
        .footer-wrapper {
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
          padding: 1rem;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-end;
          align-items: center;
          box-sizing: border-box;
        }
        .footer-wrapper > ::slotted(sh-button[slot='footer']),
        .footer-wrapper > ::slotted(sh-icon[slot='footer']),
        .label-wrapper > ::slotted([slot='functions']) {
          margin-left: 0.5rem;
        }
        .pane-label {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: var(--text-primary);
        }
        .header-wrapper > ::slotted(sh-tabs[slot='header']) {
          margin: -1rem 0 0 0;
        }
        .pane-wrapper[empty-header] .header-wrapper {
          display: none;
        }
        .pane-wrapper[empty-footer] .footer-wrapper {
          display: none;
        }
        .pane-wrapper:not([empty-header]) .label-wrapper {
          display: none;
        }
        .pane-wrapper:not([empty-header]) .body-wrapper {
          padding-top: 0rem;
        }
        .pane-wrapper:not([empty-footer]) .body-wrapper {
          padding-bottom: 0rem;
        }

        /* collapse / condense behavior */
        :host([size='expanded']) .pane-wrapper {
          max-width: 22rem;
        }
        :host([size='collapsed']) .pane-wrapper {
          max-width: 0rem;
        }
        :host([size='condensed']) .pane-wrapper {
          max-width: 6.25rem;
        }

        :host([size$='expanded']) .transition,
        :host([size$='px']) .transition,
        :host([size$='%']) .transition,
        :host([size$='rem']) .transition,
        :host([size$='vw']) .transition {
          transition: var(--transition-time) opacity ease-in-out var(--transition-time);
        }
        :host([size='expanded']) .label-wrapper,
        :host([size='expanded']) .header-wrapper,
        :host([size='expanded']) .body-wrapper,
        :host([size='expanded']) .footer-wrapper {
          width: 22rem;
        }
        :host(:not([size='expanded']):not([size='resizable']):not([size*='px']):not([size*='rem']):not([size*='%']):not([size*='vw']))
          .label-wrapper,
        :host(:not([size='expanded']):not([size='resizable']):not([size*='px']):not([size*='rem']):not([size*='%']):not([size*='vw']))
          .header-wrapper,
        :host(:not([size='expanded']):not([size='resizable']):not([size*='px']):not([size*='rem']):not([size*='%']):not([size*='vw']))
          .body-wrapper,
        :host(:not([size='expanded']):not([size='resizable']):not([size*='px']):not([size*='rem']):not([size*='%']):not([size*='vw']))
          .footer-wrapper {
          width: 0rem;
          max-height: 0rem;
          opacity: 0;
          transition: 0s all;
          pointer-events: none;
        }
        :host([size='condensed']) .condensed-wrapper {
          transition: var(--transition-time) opacity ease-in-out var(--transition-time);
          height: 100%;
          position: absolute;
          width: 6.25rem;
          padding: 1rem;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          align-items: center;
          overflow-y: auto;
        }
        :host(:not([size='condensed'])) .condensed-wrapper {
          opacity: 0;
          width: 0rem;
          height: 0rem;
        }

        /* arrow wrapper */
        :host(:not([behavior])) .arrow-wrapper {
          display: none;
        }
        .arrow-wrapper {
          padding: 0.25rem 0;
          position: absolute;
          transition: var(--transition-time) all ease-in-out;
          background-color: var(--base-2);
          transform: translateZ(0);
        }
        :host([slot='left']) .arrow-wrapper {
          right: -2rem;
          box-shadow: 0.125rem 0rem 0.25rem rgba(0, 0, 0, 0.1);
          border-radius: 0rem 0.125rem 0.125rem 0rem;
        }
        :host([slot='right']) .arrow-wrapper {
          left: -2rem;
          box-shadow: -0.125rem 0rem 0.25rem rgba(0, 0, 0, 0.1);
          border-radius: 0.125rem 0rem 0rem 0.125rem;
        }

        /* arrow position */
        :host([arrow-position='top']) .arrow-wrapper {
          top: 0.75rem;
        }
        :host([arrow-position='bottom']) .arrow-wrapper {
          bottom: 0.75rem;
        }
        :host([arrow-position='center']) .arrow-wrapper {
          top: calc(50% - 1.25rem);
        }

        /* arrow */
        :host([slot='right'][size='condensed'][behavior='condense']) .arrow,
        :host([slot='right'][size='collapsed'][behavior='collapse']) .arrow,
        :host([slot='right'][size='collapsed'][behavior='condense']) .arrow,
        :host([slot='left'][size='condensed'][behavior='collapse']) .arrow,
        :host([slot='left'][size='expanded']) .arrow,
        :host([slot='left'][size$='px']) .arrow,
        :host([slot='left'][size$='rem']) .arrow,
        :host([slot='left'][size$='vw']) .arrow,
        :host([slot='left'][size$='%']) .arrow {
          transform: rotateY(180deg);
          -webkit-transform: rotateY(180deg);
        }

        .resize-handle:active,
        .resize-handle,
        .resize-handle:hover {
          position: absolute;
          height: 100%;
          width: 0.125rem;
          display: block;
          cursor: ew-resize;
        }
        /* positioning */
        :host([slot='left']) .resize-handle {
          right: 0rem;
        }
        :host([slot='right']) .resize-handle {
          left: 0rem;
        }
        :host([size='resizable']) .label-wrapper,
        :host([size='resizable']) .header-wrapper,
        :host([size='resizable']) .body-wrapper,
        :host([size='resizable']) .footer-wrapper,
        :host([size='resizable']) .condensed-wrapper {
          max-width: unset;
          width: 100%;
          -webkit-transition: all 0s linear;
          -o-transition: all 0s linear;
          transition: all 0s linear;
        }
        :host([size='resizable']) {
          -webkit-transition: all 0s linear;
          -o-transition: all 0s linear;
          transition: all 0s linear;
          width: -webkit-fit-content;
          width: -moz-fit-content;
          width: fit-content;
          min-width: -webkit-fit-content;
          min-width: -moz-fit-content;
          min-width: fit-content;
        }
        :host([size='resizable']) .pane-wrapper {
          max-width: unset;
          -webkit-transition: all 0s linear;
          -o-transition: all 0s linear;
          transition: all 0s linear;
          --resized-width: 22rem;
          width: var(--resized-width);
          min-width: var(--resized-width);
        }
        :host(:not([size='resizable'])) .resize-handle {
          display: none;
        }
        :host([is-Resize]) {
          cursor: ew-resize;
        }
        :host(:not([label])) .label-wrapper {
          display: none;
        }
        :host([label]) .body-wrapper {
          padding-top: 0rem;
        }
      `,
    ];
  }
  render() {
    return html`
      <div
        class="pane-wrapper"
        ?empty-footer="${this._emptyFooter}"
        ?empty-header="${this._emptyHeader}"
        ?is-Resize="${this._isResize}"
        style="${styleMap(this._getWidth())}"
      >
        <div class="header-wrapper transition">
          <slot
            name="header"
            id="header"
            @slotchange="${(e) => this._updateHeaderSlotFlag(e.target)}"
          ></slot>
        </div>
        ${this.label === '' || this.label === undefined
          ? ''
          : html`${!this._emptyHeader
              ? ''
              : html` <div class="label-wrapper transition">
                  <sh-text size="super-header" class="pane-label">${this.label}</sh-text>
                  <slot name="functions" id="functions"></slot>
                </div>`}`}

        <div class="body-wrapper transition">
          <slot id="body"></slot>
        </div>
        <div class="footer-wrapper transition">
          <slot
            name="footer"
            id="footer"
            @slotchange="${(e) => this._updateFooterSlotFlag(e.target)}"
          ></slot>
        </div>
        ${this.behavior === 'condense' || this.size === 'condensed'
          ? html` <div class="condensed-wrapper">
              <slot name="condensed" id="condensed"></slot>
            </div>`
          : ''}
        <div class="arrow-wrapper" @click="${() => this._handleExpand()}">
          <sh-icon icon="arrow-right-s" button="" class="arrow"></sh-icon>
        </div>
        <div class="resize-handle"></div>
      </div>
    `;
  }
  constructor() {
    super();
    this.size = 'expanded';
    this.arrowPosition = 'center';
    this._emptyHeader = true;
    this._emptyFooter = true;
    this._isResize = false;
  }
  firstUpdated() {
    /** @ignore */
    this.shadowRoot.querySelector('.resize-handle').onmousedown = () => {
      // removing previously added events to body
      this._removeEventListenersFromDocument();
      // adding new events to body
      if (this.size === 'resizable') {
        this._addEventListenersToDocument();
      }
    };
  }
  connectedCallback() {
    super.connectedCallback();
    /** @ignore */
    this._documentMouseDownListener = this._documentMouseDownActions.bind(this);
    /** @ignore */
    this._documentMouseUpListener = this._documentMouseUpActions.bind(this);
    /** @ignore */
    this._documentMouseMoveListener = this._documentMouseMoveActions.bind(this);
    /** @ignore */
    this._documentMouseOverListener = this._documentMouseOverActions.bind(this);
    /** @ignore */
    this._documentDragStartListener = this._documentDragStartActions.bind(this);
    /** @ignore */
    this._body = document.body;
    /** @ignore */
    this._resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this._hundredPercentWidth = entry.contentRect.width;
      }
    });
    this._resizeObserver.observe(this._body);
  }
  disconnectedCallback() {
    this._removeEventListenersFromDocument();
    this._resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  updated(changedProperties) {
    const listOfProperties = ['label', 'size', 'arrowPosition', 'behavior'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
    if (changedProperties.has('size')) {
      this._updateSize();
    }
  }
  _getWidth() {
    if (this.size === 'expanded' || this.size === 'resizable') {
      return { width: '22rem' };
    } else {
      return { width: this._fixSize };
    }
  }
  _updateSize() {
    //test for valid set sizes (px, rem, %)
    if (/[0-9]*\.?[0-9]+(px|rem|vw)/.test(this.size)) {
      this._fixSize = this.size;
    } else if (/[0-9]*\.?[0-9]+(%)/.test(this.size)) {
      this._fixSize = `${parseInt(this.size)}vw`;
    } else if (this.size === 'expanded' || this.size === 'resizable') {
      this._fixSize = null;
    }
  }
  _updateHeaderSlotFlag(slot) {
    const headerNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    this._emptyHeader = headerNodes === 0;
  }
  _updateFooterSlotFlag(slot) {
    const headerNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    this._emptyFooter = headerNodes === 0;
  }
  _documentMouseDownActions(e) {
    if (e.composedPath()[0] === this.shadowRoot.querySelector('.resize-handle')) {
      this._isResize = true;
    }
  }
  _documentMouseMoveActions(e) {
    this._setPaneWidth(e);
  }
  _documentMouseOverActions(e) {
    this._setPaneWidth(e);
  }
  _documentMouseUpActions() {
    this._removeEventListenersFromDocument();
    this._isResize = false;
  }
  _documentDragStartActions(e) {
    if (this._isResize) {
      e.preventDefault();
    }
  }
  _setPaneWidth(e) {
    const isLeftPane = this.getAttribute('slot') === 'left' ? true : false;
    const defaultPane = 22;
    if (this._isResize) {
      let resizedWidth;
      if (isLeftPane) {
        resizedWidth = e.clientX;
      } else {
        resizedWidth = this._hundredPercentWidth - e.clientX;
      }
      if (resizedWidth >= document.body.clientWidth * 0.75) {
        resizedWidth = document.body.clientWidth * 0.75;
      }
      // converting 352px to its equal value in rem (22rem) and comparing if the rem value of the
      // current value is below 22
      else if (resizedWidth / baseFontSize <= defaultPane) {
        // converting the rem value to px here so that it can be re-converted to rem below
        resizedWidth = defaultPane * baseFontSize;
      }
      this.shadowRoot
        .querySelector('.pane-wrapper')
        .style.setProperty('--resized-width', `${resizedWidth / baseFontSize}rem`);
    }
  }
  _addEventListenersToDocument() {
    document.body.addEventListener('mousedown', this._documentMouseDownListener);
    document.body.addEventListener('mouseup', this._documentMouseUpListener);
    document.body.addEventListener('mousemove', this._documentMouseMoveListener);
    document.body.addEventListener('mouseover', this._documentMouseOverListener);
    document.body.addEventListener('dragstart', this._documentDragStartListener);
  }
  _removeEventListenersFromDocument() {
    document.body.removeEventListener('mouseup', this._documentMouseUpListener);
    document.body.removeEventListener('mousedown', this._documentMouseDownListener);
    document.body.removeEventListener('mousemove', this._documentMouseMoveListener);
    document.body.removeEventListener('mouseover', this._documentMouseOverListener);
    document.body.removeEventListener('dragstart', this._documentDragStartListener);
  }
  // switches expanded prop on button click
  _handleExpand() {
    if (this.size === 'expanded' || /[0-9]*\.?[0-9]+(px|%|rem|vw)/.test(this.size)) {
      //close pane
      if (this.behavior === 'condense') {
        this.size = 'condensed';
      } else if (this.behavior === 'collapse') {
        this.size = 'collapsed';
      }
    } else if (this.size === 'collapsed' && this.behavior === 'condense') {
      this.size = 'condensed';
      this.behavior = 'collapse';
    } else if (this.size === 'condensed' && this.behavior === 'collapse') {
      this.size = 'collapsed';
      this.behavior = 'condense';
    } else if (this._fixSize) {
      this.size = this._fixSize;
    } else {
      //open
      this.size = 'expanded';
    }
  }
}
customElements.define('sh-pane', SHPane);
